import React, { useState, useEffect, useRef } from 'react';
import { X, ExternalLink, Sparkles, Volume2, VolumeX } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { resolveMediaUrl } from '../admin/MediaUpload';

export default function FlyerAdModal() {
  const { data } = useSite();
  const flyer = data.flyerAd || {};

  const [open, setOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(flyer.duration || 6);
  const [muted, setMuted] = useState(true);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  const isEnabled = flyer.enabled !== false;
  const duration = Math.max(2, parseInt(flyer.duration, 10) || 6);
  const autoClose = flyer.autoClose !== false;

  useEffect(() => {
    if (!isEnabled) {
      setOpen(false);
      return;
    }

    // Check if dismissed in this browser session
    const dismissed = sessionStorage.getItem('flyer_ad_dismissed');
    if (dismissed === 'true') {
      return;
    }

    // Open ad with a brief, pleasant delay
    const openTimer = setTimeout(() => {
      setOpen(true);
      setTimeLeft(duration);
    }, 600);

    return () => clearTimeout(openTimer);
  }, [isEnabled, duration]);

  // Countdown timer & auto-close logic
  useEffect(() => {
    if (!open || !autoClose) return;

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open, autoClose]);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem('flyer_ad_dismissed', 'true');
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleActionClick = () => {
    handleClose();
    if (flyer.ctaLink) {
      if (flyer.ctaLink.startsWith('#')) {
        const el = document.querySelector(flyer.ctaLink);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (flyer.ctaLink.startsWith('http')) {
        window.open(flyer.ctaLink, '_blank', 'noreferrer');
      }
    }
  };

  if (!open || !isEnabled) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300 select-none"
      onClick={handleClose}
      data-testid="flyer-ad-overlay"
    >
      <div
        className="relative w-full max-w-md bg-zinc-950/95 border border-[#ccc77d]/40 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="flyer-ad-modal"
      >
        {/* Top Header Controls: Countdown Timer & Cross Close Icon */}
        <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
          {autoClose && (
            <span
              className="px-2.5 py-1 rounded-full bg-black/80 text-white font-mono text-[11px] font-bold border border-white/20 shadow-md backdrop-blur-xs flex items-center gap-1"
              data-testid="flyer-ad-timer"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#ccc77d] animate-ping" />
              <span>{timeLeft}s</span>
            </span>
          )}

          {/* Close cross icon */}
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close Advertisement"
            data-testid="flyer-ad-close-btn"
            className="w-8 h-8 rounded-full bg-black/80 hover:bg-black text-white hover:text-[#ccc77d] border border-white/30 flex items-center justify-center shadow-lg transition active:scale-90"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {/* Ad Media Content: Image, Video, or Stylized Header */}
        {flyer.mediaType === 'image' && flyer.imageUrl && (
          <div className="relative w-full max-h-72 bg-zinc-900 overflow-hidden flex items-center justify-center">
            <img
              src={resolveMediaUrl(flyer.imageUrl)}
              alt={flyer.title || 'Special Promotion'}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent opacity-80" />
          </div>
        )}

        {flyer.mediaType === 'video' && flyer.videoUrl && (
          <div className="relative w-full h-56 bg-black overflow-hidden flex items-center justify-center">
            <video
              ref={videoRef}
              src={resolveMediaUrl(flyer.videoUrl)}
              autoPlay
              loop
              muted={muted}
              playsInline
              className="w-full h-full object-cover"
            />
            {/* Audio Mute/Unmute Toggle */}
            <button
              type="button"
              onClick={() => {
                setMuted(!muted);
                if (videoRef.current) videoRef.current.muted = !muted;
              }}
              className="absolute bottom-2.5 right-2.5 z-20 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center border border-white/20"
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
          </div>
        )}

        {flyer.mediaType === 'text' && (
          <div className="w-full py-6 px-4 bg-gradient-to-r from-[#777544] via-[#5b5934] to-[#3e3d26] flex items-center justify-center gap-2 border-b border-[#ccc77d]/30">
            <Sparkles size={24} className="text-[#ccc77d] shrink-0 animate-pulse" />
            <span className="text-white font-extrabold uppercase tracking-wider text-sm sm:text-base">
              OFFICIAL ANNOUNCEMENT
            </span>
          </div>
        )}

        {/* Ad Text & CTA Body */}
        <div className="p-5 text-center space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-[#ccc77d]/20 border border-[#ccc77d]/40 text-[#ccc77d] text-[10px] uppercase tracking-wider font-extrabold">
            <Sparkles size={11} />
            <span>Featured Offer</span>
          </div>

          <h3 className="text-white font-black text-lg sm:text-xl tracking-tight leading-snug">
            {flyer.title || 'Exclusive Welcome Promotion!'}
          </h3>

          {flyer.message && (
            <p className="text-zinc-300 text-xs sm:text-sm leading-relaxed max-w-sm mx-auto">
              {flyer.message}
            </p>
          )}

          {/* Action CTA Button */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleActionClick}
              data-testid="flyer-ad-cta-btn"
              className="w-full h-10 px-5 rounded-full bg-[#ccc77d] hover:bg-[#d8d387] text-black font-black text-xs uppercase tracking-wider shadow-lg hover:shadow-xl transition active:scale-95 flex items-center justify-center gap-2"
            >
              <span>{flyer.ctaText || 'CLAIM OFFER NOW'}</span>
              <ExternalLink size={14} className="text-black shrink-0" />
            </button>
          </div>

          {/* Footer auto-close hint */}
          {autoClose && (
            <div className="text-[10px] text-zinc-500 font-mono">
              Auto closing in {timeLeft} seconds...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
